import React, { useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import animationData from "../../../../../assets/illustrations/hero_sec_animation.json";
import Lottie from "react-lottie";

const BackgroundPattern: React.FC = () => {
  return (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        opacity: 0.3,
        zIndex: 0,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(0, 11, 24, 0.7) 0%, rgba(0, 30, 60, 0.7) 100%)",
          zIndex: 1,
        },
      }}
    >
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern
            id="grid"
            width="60"
            height="60"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 60 0 L 0 0 0 60"
              fill="none"
              stroke="rgba(6, 182, 212, 0.5)"
              strokeWidth="1"
            />
          </pattern>
          <pattern
            id="dots"
            width="30"
            height="30"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="3" cy="3" r="1.5" fill="rgba(6, 182, 212, 0.4)" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
        <rect width="100%" height="100%" fill="url(#dots)" />
      </svg>
    </Box>
  );
};

const GlowingText: React.FC<{ text: string; delay?: number }> = ({
  text,
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
    >
      <motion.div
        animate={{
          textShadow: [
            "0 0 20px rgba(6, 182, 212, 0.5)",
            "0 0 40px rgba(6, 182, 212, 0.7)",
            "0 0 20px rgba(6, 182, 212, 0.5)",
          ],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        {text}
      </motion.div>
    </motion.div>
  );
};

const ActionButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary";
}> = ({ children, onClick, variant = "primary" }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      style={{
        background:
          variant === "primary"
            ? "linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)"
            : "transparent",
        border:
          variant === "primary"
            ? "1px solid rgba(6, 182, 212, 0.5)"
            : "1px solid rgba(255, 255, 255, 0.2)",
        padding: "16px 32px",
        borderRadius: "8px",
        color: "white",
        fontSize: "16px",
        fontWeight: "500",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        backdropFilter: "blur(10px)",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <motion.div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            variant === "primary"
              ? "linear-gradient(45deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))"
              : "linear-gradient(45deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02))",
          opacity: 0,
        }}
        animate={{
          opacity: [0, 0.5, 0],
          x: ["-100%", "100%"],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: "linear",
        }}
      />
      {children}
    </motion.button>
  );
};

const DataFlowAnimation: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "250px" : "400px",
          height: isMobile ? "250px" : "400px",
          background:
            "radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0) 70%)",
          borderRadius: "50%",
          filter: "blur(20px)",
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Lottie
          options={{
            animationData: animationData,
            loop: true,
            autoplay: true,
            rendererSettings: {
              preserveAspectRatio: "xMidYMid slice",
            },
          }}
          width={isMobile ? 300 : 500}
          height={isMobile ? 300 : 500}
          style={{
            maxWidth: "100%",
            height: "auto",
          }}
        />
      </Box>
    </Box>
  );
};

export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={containerRef}
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #000B18 0%, #001E3C 100%)",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        pt: isMobile ? 8 : 0,
        pb: isMobile ? 8 : 0,
      }}
    >
      <BackgroundPattern />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                textAlign: "left",
                color: "white",
                gap: 4,
                maxWidth: "800px",
              }}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.125rem" },
                    color: "#06B6D4",
                    fontWeight: 500,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    mb: 2,
                    fontFamily: "Montserrat",
                  }}
                >
                  Enterprise Resource Planning
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: {
                      xs: "2rem",
                      sm: "2.5rem",
                      md: "3.5rem",
                      lg: "4rem",
                    },
                    fontWeight: 700,
                    lineHeight: 1.2,
                    mb: 3,
                    background:
                      "linear-gradient(135deg, #FFFFFF 0%, rgba(255, 255, 255, 0.85) 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontFamily: "Montserrat",
                  }}
                >
                  Transform Your Business with Intelligence
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1rem", md: "1.25rem" },
                    color: "rgba(255, 255, 255, 0.8)",
                    lineHeight: 1.6,
                    mb: 4,
                    maxWidth: "600px",
                    fontFamily: "Montserrat",
                  }}
                >
                  Streamline operations, enhance productivity, and drive growth
                  with our advanced ERP solution. Experience seamless
                  integration of business processes in real-time.
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                style={{
                  display: "flex",
                  gap: "1rem",
                  flexDirection: isMobile ? "column" : "row",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <ActionButton
                  onClick={() => navigate("/demo")}
                  variant="primary"
                >
                  Schedule Demo
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </ActionButton>
                <ActionButton
                  onClick={() => navigate("/features")}
                  variant="secondary"
                >
                  View Features
                </ActionButton>
              </motion.div>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <DataFlowAnimation />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
